import Button from './ui/Button'
import ar from '../locales/ar.json'
import { AlertTriangle, ChevronRight } from 'lucide-react'

const t  = ar.review
const ta = ar.app

function Section({ title, step, onEdit, children }) {
  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden mb-4">
      <div className="flex items-center justify-between bg-stone-50 px-4 py-3 border-b border-stone-200">
        <h3 className="font-semibold text-stone-800 text-sm">{title}</h3>
        <button
          type="button"
          onClick={() => onEdit(step)}
          className="text-xs text-primary-600 hover:text-primary-800 font-medium"
          aria-label={`تعديل ${title}`}
        >
          ✏ {t.edit}
        </button>
      </div>
      <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        {children}
      </div>
    </div>
  )
}

function Row({ label, value }) {
  if (!value && value !== false) return null
  const display = typeof value === 'boolean' ? (value ? 'نعم' : 'لا')
                : Array.isArray(value) ? value.join('، ')
                : String(value)
  return (
    <>
      <span className="text-stone-500">{label}</span>
      <span className="text-stone-900 font-medium">{display || '—'}</span>
    </>
  )
}

export default function ReviewScreen({ formData: f, files, onEdit, onConfirm, onBack, loading }) {
  const canSubmit = f.affiliationDeclaration && f.final_signature?.trim()

  return (
    <div className="max-w-3xl mx-auto">
      <div className="card mb-6">
        <h2 className="text-xl font-bold text-primary-700 mb-1">{t.title}</h2>
        <p className="text-stone-500 text-sm">{t.subtitle}</p>
      </div>

      {/* Step 1 */}
      <Section title={ar.steps[1].title} step={1} onEdit={onEdit}>
        <Row label="مقدم الطلب"    value={f.name_applicant} />
        <Row label="صلة القرابة"    value={f.relation_applicant} />
        <Row label="هاتف أساسي"    value={f.phone_primary} />
        <Row label="هاتف ثانوي"    value={f.phone_secondary} />
        <Row label="المنطقة"       value={f.area_applicant} />
      </Section>

      {/* Step 2 */}
      <Section title={ar.steps[2].title} step={2} onEdit={onEdit}>
        <Row label="اسم الشهيد"    value={f.martyr_fullname} />
        <Row label="اسم الأم"       value={f.mother_name} />
        <Row label="تاريخ الميلاد" value={f.birth_date} />
        <Row label="الحالة الاجتماعية" value={f.marital_status} />
        <Row label="آخر مكان إقامة"  value={f.current_residence} />
        <Row label="مكان النفوس"    value={f.civil_registry_place} />
        {files?.martyr_photo_upload && <Row label="الصورة" value={files.martyr_photo_upload.name} />}
        <Row label="إذن النشر"      value={f.photo_publish_consent} />
      </Section>

      {/* Step 3 */}
      <Section title={ar.steps[3].title} step={3} onEdit={onEdit}>
        <Row label="نوع الحادثة"   value={f.incident_type} />
        {Object.entries(f.incidentDetails || {}).map(([k, v]) => (
          <Row key={k} label={k.replace(/_/g, ' ')} value={v} />
        ))}
        <Row label="أنواع الأدلة"  value={f.evidence_types} />
        {files?.evidence_upload?.length > 0 && (
          <Row label="ملفات محملة" value={files.evidence_upload.map((f) => f.name).join('، ')} />
        )}
      </Section>

      {/* Step 4 */}
      <Section title={ar.steps[4].title} step={4} onEdit={onEdit}>
        <Row label="يوجد شهود؟"    value={f.has_witnesses} />
        {f.witnesses?.map((w, i) => (
          <Row key={i} label={`شاهد ${i + 1}`} value={`${w.witness_name} — ${w.witness_role}`} />
        ))}
      </Section>

      {/* Step 5 */}
      <Section title={ar.steps[5].title} step={5} onEdit={onEdit}>
        <Row label="يوجد زوج/زوجة" value={f.spouseExists} />
        {f.spouseExists === 'نعم' && (
          <>
            <Row label="اسم الزوج/ة"    value={f.spouse_fullname} />
            <Row label="مكان الإقامة"   value={f.spouse_current_location} />
          </>
        )}
        <Row label="عدد الأبناء"        value={f.children?.length || 0} />
        <Row label="احتياجات الدعم"     value={f.family_support_needs} />
        <Row label="جهة التواصل"        value={f.case_contact_person} />
        <Row label="هاتف التواصل"       value={f.contact_primary_phone} />
      </Section>

      {/* Step 6 */}
      <Section title={ar.steps[6].title} step={6} onEdit={onEdit}>
        <Row label="المنطقة"            value={f.region} />
        <Row label="الحي"               value={f.neighborhood} />
        <Row label="الإقرار"            value={f.affiliationDeclaration} />
        <Row label="التوقيع"            value={f.final_signature} />
        <Row label="التاريخ"            value={f.final_date} />
      </Section>

      {/* Actions */}
      <div className="card">
        {!canSubmit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
            يجب الموافقة على الإقرار وتوقيع الاسم الكامل قبل التقديم (الخطوة 6)
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button variant="secondary" onClick={onBack} className="flex items-center gap-1.5">
            <ChevronRight size={16} />
            تعديل
          </Button>
          <div className="flex gap-3">
            <Button
              variant="gold"
              onClick={onConfirm}
              disabled={!canSubmit}
              loading={loading}
            >
              {t.confirmSubmit}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
